import React, { useState, ReactNode, useRef } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { Flex, Box } from 'mdlkit';
import { FocusButtonLink } from 'components/shared/Styles';
import { TOO_MANY_REQUESTS } from '../../constants/errors';
import { Chevron } from '../../core/icons';
import AuthCodeSessionForm from '../../components/login/AuthCodeSessionForm';
import ResendCodeFormNew from '../../components/login/ResendCodeForm/ResendCodeFormNew';
import {
  AuthCode,
  AuthCodeResponse,
  AuthCodeSession,
  AuthCodeSessionResponse, // eslint-disable-line @typescript-eslint/no-unused-vars
} from '../../interfaces/login';
import createAuthCodeSessionAsync from '../../api/login/createAuthCodeSessionAsync';
import createAuthCodeAsync from '../../api/login/createAuthCodeAsync';
import { CallCustomerService } from '../../components/shared';
import toast from '../../utils/toast';
import getResendCodeMessage from '../../utils/resendCodeMessage';

interface Props {
  headline: string | ReactNode;
  subHeadline: string | ReactNode;
  resendCodeCta: string | ReactNode;
  authCodeRes: AuthCodeResponse;
  onSuccess: (AuthCodeSession: AuthCodeSessionResponse) => void;
  onResendCode: (authCode: AuthCodeResponse) => void;
  lockedUser?: boolean;
  trackId?: string;
}

const StyledFlex = styled(Flex)`
  flex-direction: column;
  position: relative;
`;

const StyledButtonLink = styled(FocusButtonLink)`
  margin-top: 20px;
`;

const AuthCodeVerificationContainer = ({
  headline,
  subHeadline,
  resendCodeCta,
  authCodeRes,
  onSuccess,
  onResendCode,
  lockedUser,
  trackId = '',
}: Props) => {
  const {
    id: authCodeId,
    user: { id },
  } = authCodeRes;
  const [showResendCode, setShowResendCode] = useState<boolean>(false);

  const { formatMessage: f } = useIntl();
  const connectionRef = useRef<string>('');
  const authCodeResValue = authCodeRes.connection?.toString();

  const handleVerifyAuthCode = async (
    values: AuthCodeSession
  ): Promise<any> => {
    const { authCodeSession, error } = await createAuthCodeSessionAsync({
      ...values,
      ...(lockedUser && { lockedUser }),
    });
    if (authCodeSession) {
      window.createPageActionEvent({
        controlName: `${trackId}-success`,
        controlText: 'Verify',
        attributes: [
          {
            key: 'verifyType',
            value: connectionRef.current
              ? connectionRef.current
              : authCodeResValue,
          },
        ],
      });
      onSuccess(authCodeSession);
      return undefined;
    }

    if (error) {
      const actionData = {
        controlName: `${trackId}-attempt`,
        controlText: 'Verify',
        attributes: [
          {
            key: 'verifyType',
            value: connectionRef.current
              ? connectionRef.current
              : authCodeResValue,
          },
        ],
      };
      const errorData = {
        errorLocation: 'Login-Submit',
        errorType: 'Internal_API',
        errorCode: error?.code ?? '',
        errorCause: '',
      };

      if (error?.payload) {
        errorData.errorCause = error.payload;
        window.createErrorEvent(actionData, errorData);
        return error.payload;
      }

      errorData.errorCause = f({ id: 'error.unexpectedError' });
      window.createErrorEvent(actionData, errorData);
      return toast.error(
        <CallCustomerService message={f({ id: 'error.unexpectedError' })} />
      );
    }

    return undefined;
  };

  const handleResendCode = async (values: AuthCode) => {
    const { authCode, error } = await createAuthCodeAsync(values);
    if (authCode) {
      connectionRef.current = values.connection;
      onResendCode(authCode);
      toast.success(f({ id: getResendCodeMessage(values.connection) }));
      return;
    }

    let errorCause = '';
    if (error?.code === TOO_MANY_REQUESTS) {
      errorCause = f({ id: 'login.accountRecovery.failed.tooManyRequests' });
      toast.error(f({ id: 'login.accountRecovery.failed.tooManyRequests' }));
    } else {
      errorCause = f({ id: 'accountRecovery.resendCodeError' });
      toast.error(
        <CallCustomerService
          message={f({ id: 'accountRecovery.resendCodeError' })}
        />
      );
    }

    window.createErrorEvent(
      {
        controlName: trackId,
        controlText: 'Resend code',
        attributes: [{ key: 'connection', value: values.connection }],
      },
      {
        errorLocation: 'Login-Submit',
        errorType: 'Internal_API',
        errorCode: error?.code ?? '',
        errorCause,
      }
    );
  };

  return (
    <StyledFlex>
      {headline}
      {subHeadline}
      <AuthCodeSessionForm
        authCodeId={authCodeId}
        userId={id}
        onSubmit={handleVerifyAuthCode}
        buttonAtEnd
      />

      <StyledButtonLink onClick={() => setShowResendCode((prev) => !prev)}>
        {resendCodeCta}
        <Chevron color="primary" mt="xxs" ml="xxs" isOpen={!showResendCode} />
      </StyledButtonLink>

      <Box mt={20} mb={75}>
        <ResendCodeFormNew
          isOpen={showResendCode}
          authCodeRes={authCodeRes}
          onResendCode={handleResendCode}
        />
      </Box>
    </StyledFlex>
  );
};

export default AuthCodeVerificationContainer;
